import type { TimelineItem } from '@sd/client-core';

/**
 * Key of the first message or system line. A date divider keeps its key when
 * an older page of the same day is prepended, so it cannot tell that the top
 * of the list changed.
 */
export function firstContentKey(items: readonly TimelineItem[]): string | null {
  return items.find((item) => item.kind === 'message' || item.kind === 'system')?.key ?? null;
}

/** With unread messages a conversation opens at the "여기서부터 안 읽은 메시지" divider, otherwise at the newest message. */
export function openingPosition(items: readonly TimelineItem[]): 'unread' | 'bottom' {
  return items.some((item) => item.kind === 'unread') ? 'unread' : 'bottom';
}

/** Most rows that may rise into place at once; more are a page coming in (catching up, "최근 메시지로"), not new messages. */
export const MAX_ARRIVALS = 3;

/**
 * Message keys that came in at the bottom since `known` was drawn: after the newest known one and not known
 * themselves. An older page above, or nothing drawn yet (`known` null: the conversation is opening), brings none.
 */
export function arrivedAtBottom(known: ReadonlySet<string> | null, keys: readonly string[]): string[] {
  if (!known) return [];
  let last = -1;
  keys.forEach((key, index) => {
    if (known.has(key)) last = index;
  });
  const fresh = keys.slice(last + 1).filter((key) => !known.has(key));
  return fresh.length <= MAX_ARRIVALS ? fresh : [];
}
